import { useEffect, type RefObject } from 'react'
import { currentRange } from '../../components/editor/caret'

/**
 * Focus mode lights one block of the page and fades the rest to --wr-dim, as
 * the reader's Reading light does. Which block, in order (Niki, 10/1):
 * 1. where the writer is typing: the block holding the caret;
 * 2. else where the mouse is: the block under it, or the nearest within a
 *    paragraph gap (as the reader's light follows the cursor);
 * 3. else the block across the middle of the screen.
 * Nothing scrolls on its own, so any block can be lit, the title at the top
 * of the page included.
 */

/** The writing tool bar pinned over the page (ComposerToolBar, h-12). */
const BAR = 48
/** How far above or below a block the mouse can be and still light it (the gap between paragraphs). */
const NEAR_BLOCK = 48
/** How far beside the column the mouse can be (into the margins, where notes sit) and still light a block. */
const NEAR_SIDE = 260

/**
 * A block of the page that can be in focus: a row of the column, or a piece
 * of the header (title, byline…). A Columns group lights as one (it's a
 * focus unit itself), so the rows in its cells don't count on their own.
 */
const UNITS = '[data-row]:not([data-cols] [data-row]), [data-focus-unit]'

/** The middle of the screen below the tool bar: the fallback when there's no caret and no mouse. */
function centreY(): number {
  const vh = window.innerHeight || 800
  return BAR + (vh - BAR) / 2
}

/** The unit across the line, or else the nearest one to it. */
export function unitAtLine(units: HTMLElement[], line: number): HTMLElement | null {
  let best: HTMLElement | null = null
  let bestDist = Infinity
  for (const el of units) {
    const r = el.getBoundingClientRect()
    if (r.height === 0 && r.width === 0) continue
    const dist = line < r.top ? r.top - line : line > r.bottom ? line - r.bottom : 0
    if (dist < bestDist) {
      best = el
      bestDist = dist
      if (dist === 0) break
    }
  }
  return best
}

/**
 * The unit the mouse at `p` points at: the one under it, else the nearest by
 * height then sideways, if it's within a paragraph gap above or below and
 * the margins' width beside. Null when the mouse is away from the writing.
 */
export function unitNear(units: HTMLElement[], p: { x: number; y: number }, near = NEAR_BLOCK, side = NEAR_SIDE): HTMLElement | null {
  let best: HTMLElement | null = null
  let bestKey: [number, number] = [Infinity, Infinity]
  for (const el of units) {
    const r = el.getBoundingClientRect()
    if (r.height === 0 && r.width === 0) continue
    const dy = p.y < r.top ? r.top - p.y : p.y > r.bottom ? p.y - r.bottom : 0
    const dx = p.x < r.left ? r.left - p.x : p.x > r.right ? p.x - r.right : 0
    if (dy < bestKey[0] || (dy === bestKey[0] && dx < bestKey[1])) {
      best = el
      bestKey = [dy, dx]
    }
  }
  return best && bestKey[0] <= near && bestKey[1] <= side ? best : null
}

/** Something on the page being typed in: a paragraph, the title, a caption, a note. */
function isTyping(el: Element | null, page: HTMLElement): el is HTMLElement {
  if (!el || !page.contains(el)) return false
  return (el as HTMLElement).isContentEditable || el.getAttribute('contenteditable') === 'true' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT'
}

/**
 * Focus mode's light (see the top of this file). The page is marked with
 * `.wr-focusing`, and units get `data-focal` or `data-dimmed` (index.css).
 * Everything is DOM-side, so the light moving never re-renders the composer.
 */
export function useFocusLine(pageRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    const page = pageRef.current
    if (!active || !page) return
    page.classList.add('wr-focusing')
    let focal: HTMLElement | null = null
    let frame = 0
    /** The mouse (or pen), while it's over the window. Touch has no hover: the caret or the centre lead there. */
    let pointer: { x: number; y: number } | null = null

    /** The unit holding the caret, while something on the page is being typed in. */
    const caretUnit = (): HTMLElement | null => {
      const typing = document.activeElement
      if (!isTyping(typing, page)) return null
      // The caret's own spot, so a Columns cell or a note lights its unit; else the field itself.
      const node = currentRange()?.startContainer ?? null
      const at = node && typing.contains(node) ? (node instanceof Element ? node : node.parentElement) : typing
      return at?.closest<HTMLElement>(UNITS) ?? null
    }

    const light = () => {
      frame = 0
      const units = Array.from(page.querySelectorAll<HTMLElement>(UNITS))
      const next = caretUnit() ?? (pointer ? unitNear(units, pointer) : null) ?? unitAtLine(units, centreY())
      if (next === focal && next?.hasAttribute('data-focal')) return
      focal = next
      for (const el of units) {
        if (el === next) {
          el.setAttribute('data-focal', '')
          el.removeAttribute('data-dimmed')
        } else {
          el.setAttribute('data-dimmed', '')
          el.removeAttribute('data-focal')
        }
      }
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(light)
    }
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      pointer = { x: e.clientX, y: e.clientY }
      schedule()
    }
    /** The mouse left the window: the centre leads until it's back. */
    const onMouseOut = (e: MouseEvent) => {
      if (e.relatedTarget) return
      pointer = null
      schedule()
    }

    light()
    document.addEventListener('selectionchange', schedule)
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    document.addEventListener('mouseout', onMouseOut)
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    page.addEventListener('input', schedule)
    // Blocks come and go as you write (a new paragraph, an insert): light again.
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(schedule)
    mo?.observe(page, { childList: true, subtree: true })

    return () => {
      if (frame) cancelAnimationFrame(frame)
      document.removeEventListener('selectionchange', schedule)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
      window.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('mouseout', onMouseOut)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      page.removeEventListener('input', schedule)
      mo?.disconnect()
      page.classList.remove('wr-focusing')
      for (const el of Array.from(page.querySelectorAll<HTMLElement>(UNITS))) {
        el.removeAttribute('data-focal')
        el.removeAttribute('data-dimmed')
      }
    }
  }, [pageRef, active])
}
